import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig } from '../shared/FormComponents';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, taskFormTourSteps } from '../shared/FeatureTour';
import FormSkeleton from '../shared/FormComponents/FormSkeleton';
import { createTaskSchema } from '../../utils/validation/form-schema';
import { fetchMatters } from '../../store/actions/Matters/MatterActions';
import { fetchOrgPeople } from '../../store/actions/OnboardingActions';
import { fetchAllTeams } from '../../store/actions/organization/teamActions';
import { fetchLinkableProjects } from '../../store/actions/Projects/ProjectActions';
import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import apis from '../../utils/apis';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { useLicenseGate } from '../../hooks/useLicenseGate';

interface TaskFormProps {
  onCancel?: () => void;
  onTaskCreated?: (task: any) => void;
  teamId?: string;
}

const TaskForm: React.FC<TaskFormProps> = ({ onCancel, onTaskCreated, teamId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { checkSectionGate } = useLicenseGate();

  const [matterOptions, setMatterOptions] = useState<DropdownOption[]>([]);
  const [personOptions, setPersonOptions] = useState<DropdownOption[]>([]);
  const [teamOptions, setTeamOptions] = useState<DropdownOption[]>([]);
  const [projectOptions, setProjectOptions] = useState<DropdownOption[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'task-form',
    steps: taskFormTourSteps,
    readyToShow: !isLoadingData,
    autoStartDelay: 600,
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoadingData(true);

        const [mattersResp, , teamsResp, projectsResp] = await Promise.all([
          dispatch(fetchMatters({ page: 1, page_size: 100 }) as any),
          dispatch(fetchOrgPeople()),
          dispatch(fetchAllTeams()),
          dispatch(fetchLinkableProjects()),
        ]);

        if (mattersResp && mattersResp.matters) {
          const formatted = mattersResp.matters.map((m: any) => ({
            value: m.uuid,
            label: m.title,
          }));
          setMatterOptions(formatted);
        }

        if (teamsResp && teamsResp.teams) {
          const formatted = teamsResp.teams.map((t: any) => ({
            value: t.LegalTeamID,
            label: t.TeamName,
          }));
          setTeamOptions(formatted);
        }

        if (projectsResp && Array.isArray(projectsResp)) {
          setProjectOptions(projectsResp.map((p: any) => ({
            value: p.uuid,
            label: p.title,
          })));
        }
      } catch (error) {
        console.error('Error loading form data:', error);
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: t('Error Loading Data'),
            subMsg: t('Failed to load form options. Please try again.'),
            modalType: 'error',
            duration: 5000,
          })
        );
      } finally {
        setIsLoadingData(false);
      }
    };

    loadData();
  }, [dispatch]);

  const handleSubmit = useCallback(
    async (formData: Record<string, any>) => {
      if (!checkSectionGate('tasks')) return;
      setIsSubmitting(true);
      try {
        // Prepare FormData for multipart request (matching AddTaskComponent pattern)
        const form = new FormData();

        // Add task fields
        form.append('title', formData.title || '');
        form.append('description', formData.description || '');
        form.append('priority', formData.priority || 'medium');
        form.append('status', formData.status || 'todo');

        // Add due_date - format the date properly
        if (formData.dueDate) {
          const dateStr = typeof formData.dueDate === 'string'
            ? formData.dueDate
            : new Date(formData.dueDate).toISOString();
          form.append('due_date', dateStr);
        }

        // Add matter_uuid if selected
        if (formData.matterUUID) {
          form.append('matter_uuid', formData.matterUUID);
        }

        // Add project_uuid if selected
        if (formData.projectUUID) {
          form.append('project_uuid', formData.projectUUID);
        }

        // Handle collaborators if any are selected
        if (formData.collaborators && formData.collaborators.length > 0) {
          const collaborators = formData.collaborators
            .map((c: any) => ({
              user_uuid: c.collaborator_uuid || String(c.user_id || ''),
              access: c.access || 'edit'
            }))
            .filter((c: any) => c.user_uuid && c.user_uuid.trim() !== '');

          if (collaborators.length > 0) {
            form.append('is_shared', 'true');
            form.append('collaborators', JSON.stringify(collaborators));
          }
        }

        // Add files if any were selected
        if (formData.files && formData.files.length > 0) {
          for (const file of formData.files) {
            form.append('files', file);
          }
        }

        // Make API call directly (matching AddTaskComponent pattern)
        const response = await apis({
          method: 'POST',
          url: '/tasks/create_task',
          data: form,
        });

        if (response && response.data && response.data.uuid) {
          dispatch(
            showFeedbackModal({
              showModal: true,
            message: t('Task Created'),
            subMsg: t('Your task has been created successfully.'),
              modalType: 'success',
              duration: 3000,
            })
          );

          if (onTaskCreated) {
            onTaskCreated(response.data);
          } else {
            onCancel?.();
          }
        }
      } catch (error: any) {
        const errorMsg = error?.response?.data?.message || t('Failed to create task');
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: t('Error Creating Task'),
            subMsg: errorMsg,
            modalType: 'error',
            duration: 5000,
          })
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [dispatch, onCancel, onTaskCreated]
  );

  if (isLoadingData) {
    return <FormSkeleton sections={2} fieldsPerSection={3} columns={2} />;
  }

  const sections: BaseFormSectionConfig[] = [
    {
      title: t('Task Details'),
      description: t('Enter the information for your task'),
      dataTourId: 'task-form-details',
      gridCols: 2,
      fields: [
        {
          name: 'title',
          type: 'input',
          label: t('Task Title'),
          placeholder: t('Enter task title'),
          required: true,
          gridSize: 'full',
          inputType: 'text',
        },
        {
          name: 'description',
          type: 'textarea',
          label: t('Description'),
          placeholder: t('Add task description and details...'),
          required: false,
          gridSize: 'full',
          rows: 4,
        },
        {
          name: 'priority',
          type: 'dropdown',
          label: t('Priority'),
          required: false,
          gridSize: 'half',
          customProps: { fullWidth: true },
          options: [
            { value: 'low', label: t('Low') },
            { value: 'medium', label: t('Medium') },
            { value: 'high', label: t('High') },
            { value: 'critical', label: t('Critical') },
          ],
        },
        {
          name: 'status',
          type: 'dropdown',
          label: t('Status'),
          required: false,
          gridSize: 'half',
          customProps: { fullWidth: true },
          options: [
            { value: 'todo', label: t('To Do') },
            { value: 'in_progress', label: t('In Progress') },
            { value: 'completed', label: t('Completed') },
            { value: 'archived', label: t('Archived') },
          ],
        },
        {
          name: 'dueDate',
          type: 'datePicker',
          label: t('Due Date'),
          required: false,
          gridSize: 'half',
          dateFormat: 'yyyy-MM-dd',
          customProps: { fullWidth: true },
        },
        {
          name: 'matterUUID',
          type: 'dropdown',
          label: t('Matter'),
          placeholder: t('Select a matter'),
          required: false,
          gridSize: 'half',
          customProps: { fullWidth: true },
          options: matterOptions,
        },
        ...(projectOptions.length > 0 ? [{
          name: 'projectUUID',
          type: 'dropdown' as const,
          label: t('Project'),
          placeholder: t('Link to a project'),
          required: false,
          gridSize: 'half' as const,
          customProps: { fullWidth: true },
          options: projectOptions,
        }] : []),
      ],
    },
    {
      title: t('Access & Files'),
      dataTourId: 'task-form-collaborators',
      gridCols: 1,
      fields: [
        {
          name: 'collaborators',
          type: 'collaboratorSelector',
          label: t('Access'),
          description: t('Grant access to people who should collaborate on this task'),
          required: false,
        },
        {
          name: 'files',
          type: 'fileUploader',
          label: t('Attachments'),
          required: false,
          maxFileSize: 52428800, // 50MB
        },
      ],
    },
  ];

  return (
    <>
    <BaseForm
      title={t('Create New Task')}
      description={t('Fill in the details to create a new task')}
      sections={sections}
      schema={createTaskSchema()}
      initialValues={{
        title: '',
        description: '',
        priority: 'medium',
        status: 'todo',
        dueDate: '',
        matterUUID: '',
        collaborators: [],
        files: [],
      }}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      submitLabel={t('Create Task')}
      cancelLabel={t('Cancel')}
      showConfirmationModal={true}
      isSubmitting={isSubmitting}
      maxWidth="max-w-4xl"
      loadingText={t('Creating task...')}
    />
    <FeatureTour
      steps={taskFormTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlay
    />
    </>
  );
};

export default TaskForm;
